import { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Calendar, Loader2, ChevronRight, GraduationCap, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import { BLOG_CATEGORY_HUB_MAP, BLOG_CATEGORY_HUBS } from "../data/blogCategoryHubs";
import { CURRICULUM_GUIDE_MAP, CURRICULUM_GUIDE_ORDER } from "../data/curriculumGuidesMap";

const SITE_URL = "https://anandtutorials.com";
const COURSE_LABELS = {
    "cbse": "CBSE (Class 1-12)",
    "icse-isc": "ICSE & ISC",
    "state-boards": "State Boards",
    "ib": "IB (PYP · MYP · DP)",
    "igcse": "IGCSE (Cambridge)",
    "a-level": "A-Level (AS & A2)",
    "ap": "AP (Advanced Placement)",
    "jee": "JEE Main & Advanced",
    "neet": "NEET Preparation",
    "ielts-toefl": "IELTS & TOEFL",
    "spoken-english": "Spoken English",
    "coding": "Coding & Computer Courses",
};

export default function BlogCategory() {
    const { slug } = useParams();
    const hub = BLOG_CATEGORY_HUB_MAP[slug];
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!hub) { setLoading(false); return; }
        api.get("/blogs")
            .then((r) => {
                const catSet = new Set(hub.categories);
                setPosts((r.data || []).filter((b) => catSet.has(b.category)));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [hub]);

    const collectionJsonLd = useMemo(() => {
        if (!hub) return null;
        return {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": `${SITE_URL}/blog/category/${hub.slug}#collection`,
            "name": hub.h1,
            "description": hub.seoDescription,
            "url": `${SITE_URL}/blog/category/${hub.slug}`,
            "isPartOf": { "@type": "Blog", "@id": `${SITE_URL}/blog#blog`, "name": "The Anand Tutorials Blog" },
            "about": hub.label,
            "hasPart": posts.slice(0, 20).map((p) => ({
                "@type": "BlogPosting",
                "headline": p.title,
                "url": `${SITE_URL}/post/${p.slug}`,
                "datePublished": p.created_at,
                "dateModified": p.updated_at || p.created_at,
                "author": { "@type": "Organization", "name": "Anand Tutorials" },
            })),
        };
    }, [hub, posts]);

    // ItemList JSON-LD — only on /blog/category/curriculum-guides. One
    // `ListItem` per curriculum in `CURRICULUM_GUIDE_ORDER`, positioned in
    // editorial order, pointing to the parent curriculum pillar. This gives
    // Google an explicit signal that the sub-hub aggregates five distinct
    // ranking entities (GCSE UK, ACARA, VCE, HSC NSW, Leaving Cert IE).
    const curriculumItemListJsonLd = useMemo(() => {
        if (!hub || hub.slug !== "curriculum-guides") return null;
        return {
            "@context": "https://schema.org",
            "@type": "ItemList",
            "@id": `${SITE_URL}/blog/category/${hub.slug}#curriculum-itemlist`,
            "name": "Curriculum deep-dive sub-hubs",
            "description": "Per-curriculum landing chips inside the Anand Tutorials Curriculum Deep-Dives hub — GCSE UK, ACARA (Australia), VCE Victoria, HSC NSW and Leaving Certificate Ireland — each linking to its parent curriculum pillar.",
            "url": `${SITE_URL}/blog/category/${hub.slug}`,
            "numberOfItems": CURRICULUM_GUIDE_ORDER.length,
            "itemListOrder": "https://schema.org/ItemListOrderAscending",
            "itemListElement": CURRICULUM_GUIDE_ORDER.map((cat, idx) => {
                const meta = CURRICULUM_GUIDE_MAP[cat];
                const inCat = posts.filter((p) => p.category === cat);
                return {
                    "@type": "ListItem",
                    "position": idx + 1,
                    "url": `${SITE_URL}${meta.pillarPath}`,
                    "name": `${meta.curriculum} · ${meta.region}`,
                    "item": {
                        "@type": "Course",
                        "@id": `${SITE_URL}${meta.pillarPath}#curriculum`,
                        "name": meta.pillarLabel,
                        "url": `${SITE_URL}${meta.pillarPath}`,
                        "description": `1:1 live online ${meta.curriculum} tuition for ${meta.region} — subject-specialist mentors, transparent pricing, free demo. ${inCat.length} editorial deep-dive${inCat.length === 1 ? "" : "s"} inside the Curriculum Deep-Dives hub.`,
                        "provider": {
                            "@type": "EducationalOrganization",
                            "@id": `${SITE_URL}/#organization`,
                            "name": "Anand Tutorials",
                            "url": SITE_URL,
                        },
                    },
                };
            }),
        };
    }, [hub, posts]);

    if (!hub) return <Navigate to="/blog" replace />;

    return (
        <div data-testid={`blog-category-${hub.slug}`}>
            <SEO
                title={hub.seoTitle}
                description={hub.seoDescription}
                path={`/blog/category/${hub.slug}`}
            />
            {collectionJsonLd && (
                <Helmet>
                    <script type="application/ld+json">{JSON.stringify(collectionJsonLd)}</script>
                </Helmet>
            )}
            {curriculumItemListJsonLd && (
                <Helmet>
                    <script type="application/ld+json" data-testid="curriculum-itemlist-jsonld">
                        {JSON.stringify(curriculumItemListJsonLd)}
                    </script>
                </Helmet>
            )}

            {/* Breadcrumbs (light background, above the dark header) */}
            <Breadcrumbs items={[{ name: "Blog", to: "/blog" }, { name: hub.label }]} />

            {/* Header */}
            <section className="bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white pt-14 pb-16">
                <div className="max-w-5xl mx-auto px-6 lg:px-8">
                    <div className="inline-flex items-center gap-1.5 bg-amber-500/95 text-[#0F172A] text-[10px] font-bold uppercase tracking-[0.22em] px-2.5 py-1 rounded-full mb-4">
                        Topic Hub · {posts.length} article{posts.length === 1 ? "" : "s"}
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-bold leading-tight mb-4">{hub.h1}</h1>
                    <p className="text-blue-100 text-lg leading-relaxed max-w-3xl">{hub.subtitle}</p>
                </div>
            </section>

            {/* Intro */}
            <section className="py-12 bg-white">
                <div className="max-w-3xl mx-auto px-6 lg:px-8">
                    <p className="text-slate-700 leading-relaxed text-lg">{hub.intro}</p>
                </div>
            </section>

            {/* Per-curriculum landing chips — only on /blog/category/curriculum-guides.
                Groups the 5 deep-dive articles under their parent curriculum, and
                links each chip to the curriculum's pillar page (bidirectional). */}
            {hub.slug === "curriculum-guides" && !loading && posts.length > 0 && (
                <section className="pb-6 bg-white" data-testid="curriculum-guides-subhub">
                    <div className="max-w-7xl mx-auto px-6 lg:px-8">
                        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-600 mb-3 flex items-center gap-2">
                            <BookOpen className="w-3 h-3" /> Sub-hubs by curriculum
                        </div>
                        <h2 className="font-heading text-2xl font-bold text-[#0F172A] mb-2">
                            Pick a curriculum — jump to the pillar or the deep-dive.
                        </h2>
                        <p className="text-slate-600 mb-6 max-w-3xl">
                            Every deep-dive below is written by a mentor who teaches that exact curriculum every week. Tap the chip to open the full curriculum pillar, or the linked article for the standalone deep-dive.
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            {CURRICULUM_GUIDE_ORDER.map((cat) => {
                                const meta = CURRICULUM_GUIDE_MAP[cat];
                                if (!meta) return null;
                                const inCat = posts.filter((p) => p.category === cat);
                                return (
                                    <div
                                        key={cat}
                                        data-testid={`curriculum-subhub-card-${meta.pillarPath.replace(/[^a-z0-9]+/gi, "-")}`}
                                        className="bg-gradient-to-br from-[#F8FAFC] to-white border border-slate-200 rounded-2xl p-5 hover:shadow-md hover:border-blue-200 transition-all"
                                    >
                                        <Link
                                            to={meta.pillarPath}
                                            data-testid={`curriculum-subhub-pillar-${meta.pillarPath.replace(/[^a-z0-9]+/gi, "-")}`}
                                            className="group inline-flex items-baseline gap-2 font-heading font-bold text-[#0F172A] hover:text-[#1E3A8A]"
                                        >
                                            <span className="text-xl leading-none">{meta.flag}</span>
                                            <span className="text-base leading-snug">{meta.pillarLabel}</span>
                                            <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-all" />
                                        </Link>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {meta.curriculum} · {meta.region} · {inCat.length} deep-dive{inCat.length === 1 ? "" : "s"}
                                        </div>
                                        {inCat.length > 0 ? (
                                            <ul className="mt-3 space-y-1.5">
                                                {inCat.map((p) => (
                                                    <li key={p.id}>
                                                        <Link
                                                            to={`/post/${p.slug}`}
                                                            data-testid={`curriculum-subhub-article-${p.slug}`}
                                                            className="text-sm text-[#1E3A8A] hover:text-amber-600 hover:underline inline-flex items-start gap-1.5 leading-snug"
                                                        >
                                                            <ArrowRight className="w-3 h-3 mt-1 shrink-0 opacity-60" />
                                                            <span>{p.title}</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-xs text-slate-400 italic mt-3">
                                                Deep-dive articles coming soon — open the pillar for the full syllabus overview.
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Post grid */}
            <section className="pb-16 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="flex items-baseline justify-between mb-6">
                        <h2 className="font-heading text-2xl font-bold text-[#0F172A]">All {hub.label} articles</h2>
                        <Link to="/blog" className="text-sm font-semibold text-[#1E3A8A] hover:text-amber-500 inline-flex items-center gap-1">
                            All categories <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                    {loading ? (
                        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-16 text-slate-500" data-testid={`blog-category-${hub.slug}-empty`}>
                            No articles yet in this topic — check back soon or browse{" "}
                            <Link to="/blog" className="text-[#1E3A8A] font-semibold underline">all articles</Link>.
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid={`blog-category-${hub.slug}-grid`}>
                            {posts.map((b) => (
                                <Link
                                    key={b.id}
                                    to={`/post/${b.slug}`}
                                    data-testid={`blog-category-${hub.slug}-card-${b.slug}`}
                                    className="group bg-[#F8FAFC] rounded-2xl border border-slate-200 overflow-hidden hover:-translate-y-1 hover:shadow-lg hover:border-blue-200 transition-all flex flex-col"
                                >
                                    {b.cover_image && (
                                        <div className="aspect-[16/9] overflow-hidden bg-slate-200">
                                            <img src={b.cover_image} alt={b.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-[#1E3A8A] rounded font-semibold">{b.category || "General"}</span>
                                            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(b.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                                        </div>
                                        <h3 className="font-heading text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-[#1E3A8A] leading-snug">{b.title}</h3>
                                        <p className="text-sm text-slate-600 leading-relaxed line-clamp-3 flex-1">{b.excerpt}</p>
                                        <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] group-hover:gap-2.5 transition-all">
                                            Read article <ArrowRight className="w-4 h-4" />
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Related course pillars + city / hub anchor */}
            <section className="py-14 bg-[#F8FAFC] border-t border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600 mb-3 flex items-center gap-2">
                        <GraduationCap className="w-3 h-3" /> Related 1:1 mentor tracks
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-[#0F172A] mb-3">Ready to talk to a mentor about {hub.label}?</h3>
                    <p className="text-slate-600 mb-6 max-w-3xl">
                        Every article on this hub is written by a senior mentor from one of the tracks below. Tap any card to see the syllabus deep-dive, sample outcomes and free-demo booking.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                        {(hub.relatedCourses || []).map((cs, i) => (
                            <Link
                                key={cs}
                                to={`/courses/${cs}`}
                                data-testid={`blog-category-${hub.slug}-course-${i}`}
                                className="group flex items-center justify-between gap-3 p-5 rounded-xl bg-white border border-emerald-100 hover:border-emerald-500 hover:shadow-sm transition-all"
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-700 flex items-center justify-center shrink-0 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                                        <GraduationCap className="w-5 h-5" />
                                    </div>
                                    <div className="font-heading font-semibold text-[#0F172A] text-sm group-hover:text-emerald-700 truncate">{COURSE_LABELS[cs] || cs}</div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-400 shrink-0 group-hover:text-emerald-600" />
                            </Link>
                        ))}
                    </div>
                    {hub.relatedCity && (
                        <Link
                            to={hub.relatedCity}
                            data-testid={`blog-category-${hub.slug}-related-city`}
                            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E3A8A] hover:text-amber-500"
                        >
                            View the full {hub.label} landing page <ArrowRight className="w-4 h-4" />
                        </Link>
                    )}
                </div>
            </section>

            {/* Sibling hubs */}
            <section className="py-14 bg-white border-t border-slate-100">
                <div className="max-w-6xl mx-auto px-6 lg:px-8">
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 mb-3">Other topic hubs</div>
                    <div className="flex flex-wrap gap-2">
                        {BLOG_CATEGORY_HUBS.filter((h) => h.slug !== hub.slug).map((h) => (
                            <Link
                                key={h.slug}
                                to={`/blog/category/${h.slug}`}
                                data-testid={`blog-category-${hub.slug}-sibling-${h.slug}`}
                                className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-[#1E3A8A] hover:text-white text-slate-700 font-semibold transition-colors"
                            >
                                {h.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
